'use client';

import type { ReactNode } from 'react';
import { showsPlanDetails } from '../health-plan/plan-details';
import { useEarlierAnswersRedirect } from '../hooks/useEarlierAnswersRedirect';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { usePlanDetailsSponsor } from '../hooks/usePlanDetailsSponsor';
import { useSignupSponsor } from '../hooks/useSignupSponsor';
import type { SignupStepId } from '../steps';
import { ReturnToStep } from './ReturnToStep';

/**
 * Sends a health-plan member back for the plan details a reload lost, once
 * the sponsor answers. The step shows meanwhile: the contact step's profile
 * save checks again before it sends, so nothing is saved without them.
 */
function PlanDetailsCheck({
  sponsor,
  children,
}: {
  sponsor: { clientId?: string; subClientId?: string };
  children: ReactNode;
}) {
  const { flow } = useLegacySignupParams();
  const read = useSignupSponsor(sponsor.clientId, sponsor.subClientId);
  if (showsPlanDetails({ healthPlan: read.data?.features.healthPlan, flow })) {
    return <ReturnToStep step="plan-details" />;
  }
  return children;
}

/**
 * Shows `step` only when every answer collected before it is there; otherwise
 * replaces it with the first step that collects a missing one. Renders nothing
 * while it decides, so no step content, error or analytics event appears.
 */
export function EarlierAnswersGate({
  step,
  children,
}: {
  step: SignupStepId;
  children: ReactNode;
}) {
  const guard = useEarlierAnswersRedirect(step);
  const planSponsor = usePlanDetailsSponsor(step);
  if (guard.kind === 'redirect') return <ReturnToStep step={guard.step} />;
  if (guard.kind === 'pending') return null;
  if (planSponsor !== null)
    return <PlanDetailsCheck sponsor={planSponsor}>{children}</PlanDetailsCheck>;
  return children;
}
